import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Lookup from '@/api/lookup/api-lookup';
import { firstListItem, apiPath } from '@/constants/defaultValues';
import {
  Get,
  GetBlob,
  Post,
  Delete,
  Put,
  PostBlob,
} from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';
import { downloadFile, getLabel } from '@/helpers/Utils';

const initialState = {
  employeeList: [],
  employeeListByEntity: [],
  employeeListByNumber: {},
  employeeByNumber: {},
  managersList: [],
  employeeFamilyInformation: [],
  employeeQaulificationDetails: [],
  employeeAppraisalDetails: [],
  employeeWorkExperienceDetails: [],
  supervisorUpdateResponse: {},
  addUpdateEmployeeResponse: {},
  addUpdateWorkExperienceResponse: {},
  addUpdateExtraFieldsResponse: {},
  addUpdateAppraisalResponse: {},
  addSaveFamilyDetailsResponse: {},
  addEmployeeQualificationDetailsResponse: {},
  addEmployeeAppraisalResponse: {},
  addEmployeeExtraFieldsResponse: {},
  employeeExtraFieldsList: [],
  notification: { show: false, code: '' },
  employeeLeaveData: {},
  selectedEmployee: {},
  loading: false,
  error: '',
};

export const getEmployeeLeaveData = createAsyncThunk(
  'employee/getEmployeeLeaveData',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/LeaveRequest/GetEmployeeLeaveData'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchEmployee = createAsyncThunk(
  'employee/fetchEmployee',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.GetEmployee(params ? params : 1);
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item.nameEn,
          nameAr: item.nameAr,
          label: item?.employeeWorkDetails?.employeeNo + ' | ' + item.name,
          employeeNo: item?.employeeWorkDetails?.employeeNo || '',
          value: item.id,
          entityID: item?.employeeWorkDetails?.entityID ?? '',
          designationID: item?.employeeWorkDetails?.designationID ?? '',
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeByID = createAsyncThunk(
  'employee/getEmployeeByID',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.GetEmployeeByIDApi(params);
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeByEntityId = createAsyncThunk(
  'employee/getEmployeeByEntityId',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.GetEmployeeByEntityId(params.Id);
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item.nameEn,
          nameAr: item.nameAr,
          label: item.employeeNo + ' | ' + getLabel(item.nameAr, item.nameEn),
          value: item.id,
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeByNumber = createAsyncThunk(
  'employee/getEmployeeByNumber',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.getEmployeeByNumber(params);
      const mappedData = {
        id: response?.data?.data?.id,
        nameEn: response?.data?.data?.nameEn,
        nameAr: response?.data?.data?.nameAr,
        label: getLabel(
          response?.data?.data?.nameAr,
          response?.data?.data?.nameEn
        ),
        value: response?.data?.data?.id,
        entityID: response?.data?.data?.employeeWorkDetails?.entityID ?? '',
        imageUrl: response?.data?.data?.employeePersonalDetails?.imageUrl ?? '',
        employeeNo: response?.data?.data?.employeeWorkDetails?.employeeNo,
      };
      return mappedData;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeByNumberforFilter = createAsyncThunk(
  'employee/getEmployeeByNumberforFilter',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.getEmployeeByNumber(params);
      const mappedData = {
        id: response?.data?.data?.id,
        nameEn: response?.data?.data?.nameEn,
        nameAr: response?.data?.data?.nameAr,
        label:
          response?.data?.data?.employeeWorkDetails?.employeeNo +
          ' | ' +
          getLabel(response?.data?.data?.nameAr, response?.data?.data?.nameEn),
        value: response?.data?.data?.id,
      };
      return mappedData;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getAllManagers = createAsyncThunk(
  'employee/getAllManagers',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/Manager/GetAll_Managers'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateSupervisor = createAsyncThunk(
  'employee/updateSupervisor',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/Manager/BatchUpdate'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addUpdateEmployee = createAsyncThunk(
  'employee/addUpdateEmployee',
  async (params, thunkAPI) => {
    try {
      const response =
        'id' in params
          ? await Put({
              url: `${apiPath}api/Lookup/Employee/Update/${params.id}`,
              data: params.request,
            })
          : await Post({
              url: `${apiPath}api/Lookup/Employee/Add`,
              data: params,
            });

      return response?.data?.errCode === '200'
        ? response.data?.data
        : response?.data?.errCode === '400' && response?.data?.data === '-10'
        ? thunkAPI.rejectWithValue(response?.data?.data)
        : thunkAPI.rejectWithValue('500');
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveEmployeeWorkExperience = createAsyncThunk(
  'employee/saveEmployeeWorkExperience',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/Employee/SaveEmployeeWorkExperience'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveEmployeeExtraFields = createAsyncThunk(
  'employee/saveEmployeeExtraFields',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/Employee/SaveEmployeeExtraFields'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveEmployeeAppraisal = createAsyncThunk(
  'employee/saveEmployeeAppraisal',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/Employee/SaveEmployeeAppraisal'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeExtraFieldsDetails = createAsyncThunk(
  'employee/getEmployeeExtraFieldsDetails',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/Employee/GetAllEmployeeExtraFields/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeFamilyDetails = createAsyncThunk(
  'employee/getEmployeeFamilyInformation',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/Employee/GetEmployeeFamilyInformation/${params.id}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveFamilyDetails = createAsyncThunk(
  `employee/SaveFamilyDetails`,
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${`api/Lookup/Employee/SaveFamilyDetails/${
          params?.id
        }/${getCompanyID()}`}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveEmployeeQualificationDetails = createAsyncThunk(
  `employee/saveEmployeeQualificationDetails`,
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}api/Lookup/Employee/SaveEmployeeQualification`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteEmployeeQualification = async (params, thunkAPI) => {
  try {
    const response = await Delete({
      url: `${apiPath}api/Lookup/Employee/DeleteEmployeeQualification/${params.id}`,
      data: params,
    });
    return response?.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message);
  }
};

export const deleteEmployeeWorkExperience = async (params, thunkAPI) => {
  try {
    const response = await Delete({
      url: `${apiPath}api/Lookup/Employee/DeleteEmployeeWorkExperience/${params.id}`,
      data: params,
    });
    return response?.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message);
  }
};

export const deleteEmployeeAppraisal = async (params, thunkAPI) => {
  try {
    const response = await Delete({
      url: `${apiPath}api/Lookup/Employee/DeleteEmployeeAppraisal/${params.id}`,
      data: params,
    });
    return response?.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message);
  }
};

export const saveEmployeeAppraisalDetails = createAsyncThunk(
  `employee/saveEmployeeAppraisalDetails`,
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}api/Lookup/Employee/SaveEmployeeAppraisal`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeWorkExperienceDetails = createAsyncThunk(
  'employee/getEmployeeWorkExperienceDetails',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/Employee/GetAllEmployeeWorkExperience/${params.id}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeQaulificationDetails = createAsyncThunk(
  'employee/getEmployeeQaulificationDetails',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/Employee/GetAllEmployeeQualification/${params.id}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeeAppraisalDetails = createAsyncThunk(
  'employee/getEmployeeAppraisalDetails',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/Employee/GetAllEmployeeAppraisal/${params.id}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// #region Image

export const GetEmployeeImage = async (params, thunkAPI) => {
  try {
    const response = await GetBlob({
      url: `${apiPath}${'api/Lookup/System/GetImage/'}EMPLOYEE/${getCompanyID()}/${params}`,
    });
    if (response) {
      return response;
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message);
  }
};

export const ExportEmployeeList = async (params, thunkAPI) => {
  try {
    const url = `${apiPath}${'api/Reporting/ExportEmployeeList'}`;
    const response = await PostBlob({
      url: url,
      data: params?.keys,
    });
    if (response) {
      downloadFile(response);
      return 1;
    } else return -1;
  } catch (error) {
    return -1;
  }
  return true;
};

// #endregion Image

const employeeSlice = createSlice({
  name: 'employee',
  initialState,
  reducers: {
    clearEmployee: (state) => {
      state.employeeListByNumber = {};
      state.selectedEmployee = {};
      state.notification = { show: false };
    },
    clearEmployees: (state) => {
      state.employeeListByNumber = {};
      state.employeeList = [];
      state.selectedEmployee = {};
      state.notification = { show: false };
    },
    clearEmployeeLeaveData: (state) => {
      state.employeeLeaveData = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployee.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(fetchEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeList = action.payload;
        state.error = '';
      })
      .addCase(fetchEmployee.rejected, (state, action) => {
        state.loading = false;
        state.employeeList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getEmployeeByEntityId.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getEmployeeByEntityId.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeListByEntity = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeByEntityId.rejected, (state, action) => {
        state.loading = false;
        state.employeeListByEntity = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getEmployeeByNumber.pending, (state) => {
        state.loading = true;
        state.employeeListByNumber = {};
        state.notification = { show: false, code: '' };
      })
      .addCase(getEmployeeByNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeListByNumber = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeByNumber.rejected, (state, action) => {
        state.loading = false;
        state.employeeListByNumber = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getEmployeeByNumberforFilter.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.employeeByNumber = {};
      })
      .addCase(getEmployeeByNumberforFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeByNumber = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeByNumberforFilter.rejected, (state, action) => {
        state.loading = false;
        state.employeeByNumber = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getAllManagers.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getAllManagers.fulfilled, (state, action) => {
        state.loading = false;
        state.managersList = action.payload;
        state.error = '';
      })
      .addCase(getAllManagers.rejected, (state, action) => {
        state.loading = false;
        state.managersList = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(updateSupervisor.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(updateSupervisor.fulfilled, (state, action) => {
        state.loading = false;
        state.supervisorUpdateResponse =
          action.payload?.length === 0 ? { id: 1 } : action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(updateSupervisor.rejected, (state, action) => {
        state.loading = false;
        state.supervisorUpdateResponse = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(addUpdateEmployee.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(addUpdateEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.addUpdateEmployeeResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(addUpdateEmployee.rejected, (state, action) => {
        state.loading = false;
        state.addUpdateEmployeeResponse = {};
        state.notification = { show: true, code: action.payload };
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(saveEmployeeWorkExperience.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(saveEmployeeWorkExperience.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.addUpdateWorkExperienceResponse = action.payload;
          if (action.payload !== '555') {
            state.notification = { show: true, code: '200' };
            state.error = '';
          }
        }
      })
      .addCase(saveEmployeeWorkExperience.rejected, (state, action) => {
        state.loading = false;
        state.addUpdateWorkExperienceResponse = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(saveEmployeeExtraFields.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(saveEmployeeExtraFields.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.addUpdateExtraFieldsResponse = action.payload;
          state.notification = { show: true, code: '200' };
          state.error = '';
        }
      })
      .addCase(saveEmployeeExtraFields.rejected, (state, action) => {
        state.loading = false;
        state.addUpdateExtraFieldsResponse = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(saveEmployeeAppraisal.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(saveEmployeeAppraisal.fulfilled, (state, action) => {
        state.loading = false;
        state.addUpdateAppraisalResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(saveEmployeeAppraisal.rejected, (state, action) => {
        state.loading = false;
        state.addUpdateAppraisalResponse = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getEmployeeExtraFieldsDetails.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getEmployeeExtraFieldsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeExtraFieldsList = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeExtraFieldsDetails.rejected, (state, action) => {
        state.loading = false;
        state.employeeExtraFieldsList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getEmployeeLeaveData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmployeeLeaveData.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeLeaveData = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeLeaveData.rejected, (state, action) => {
        state.loading = false;
        state.employeeLeaveData = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getEmployeeByID.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmployeeByID.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEmployee = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeByID.rejected, (state, action) => {
        state.loading = false;
        state.selectedEmployee = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getEmployeeFamilyDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmployeeFamilyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeFamilyInformation = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeFamilyDetails.rejected, (state, action) => {
        state.loading = false;
        state.employeeFamilyInformation = {};
        state.error = action.payload ?? 'An error occurred';
      })
      .addCase(getEmployeeQaulificationDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmployeeQaulificationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeQaulificationDetails = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeQaulificationDetails.rejected, (state, action) => {
        state.loading = false;
        state.employeeQaulificationDetails = {};
        state.error = action.payload ?? 'An error occurred';
      })
      .addCase(getEmployeeAppraisalDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmployeeAppraisalDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeAppraisalDetails = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeAppraisalDetails.rejected, (state, action) => {
        state.loading = false;
        state.employeeAppraisalDetails = {};
        state.error = action.payload ?? 'An error occurred';
      })
      .addCase(getEmployeeWorkExperienceDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmployeeWorkExperienceDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeWorkExperienceDetails = action.payload;
        state.error = '';
      })
      .addCase(getEmployeeWorkExperienceDetails.rejected, (state, action) => {
        state.loading = false;
        state.employeeWorkExperienceDetails = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(saveFamilyDetails.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(saveFamilyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.addSaveFamilyDetailsResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(saveFamilyDetails.rejected, (state, action) => {
        state.loading = false;
        state.addSaveFamilyDetailsResponse = {};
        state.error = action.payload ?? 'An error occurred';
      })
      .addCase(saveEmployeeQualificationDetails.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(saveEmployeeQualificationDetails.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.addEmployeeQualificationDetailsResponse = action.payload;
          if (action.payload !== '555') {
            state.notification = { show: true, code: '200' };
            state.error = '';
          }
        }
      })
      .addCase(saveEmployeeQualificationDetails.rejected, (state, action) => {
        state.loading = false;
        state.addEmployeeQualificationDetailsResponse = {};
        state.error = action.payload ?? 'An error occurred';
      })
      .addCase(saveEmployeeAppraisalDetails.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(saveEmployeeAppraisalDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.addEmployeeAppraisalResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(saveEmployeeAppraisalDetails.rejected, (state, action) => {
        state.loading = false;
        state.addEmployeeExtraFieldsResponse = {};
        state.error = action.payload ?? 'An error occurred';
      });
  },
});
export const { clearEmployee, clearEmployees, clearEmployeeLeaveData } =
  employeeSlice.actions;
export default employeeSlice.reducer;
